/**
 * Where a floating box goes — the one positioner in the app.
 *
 * **Why it is a module and not a method on `MenuPopup`.** It was written there first, for the
 * ⋯ menu and the right-click, and the second caller arrived the day the grid's cell picker
 * had to float over a table instead of growing inside it (`KLQ-20260828-001` §B.3). Two
 * positioners drift: one flips at the viewport edge and the other clamps, and the difference
 * shows up as "the menu behaves, the picker does not" on exactly the screens nobody resizes.
 * So the rule — *below the anchor unless there is no room below and more of it above, then
 * clamped inside a margin* — lives once, and both popups are consumers of it.
 *
 * It has no React in it on purpose. Measuring and painting belong to whoever owns the box;
 * this file only does the arithmetic, which is what makes it testable without a DOM.
 */

/** Where the box hangs from: a trigger has a rect to line up with, a right-click has a point. */
export type Anchor =
  | {
      readonly kind: "element";
      readonly rect: DOMRect;
      readonly align: "start" | "end";
    }
  | { readonly kind: "point"; readonly x: number; readonly y: number };

/** Distance between anchor and box, and the least breathing room at a viewport edge. */
export const GAP = 4;
export const MARGIN = 8;

export interface Placement {
  readonly top: number;
  readonly left: number;
}

export function place(anchor: Anchor, box: DOMRect): Placement {
  if (anchor.kind === "point") {
    // The pointer is the corner. Flipped rather than clamped when it does not fit, so a
    // right-click near the bottom of a long register opens *upwards* from the pointer instead
    // of covering the row it was aimed at.
    const down = anchor.y;
    const up = anchor.y - box.height;
    const top =
      down + box.height > window.innerHeight - MARGIN && up > MARGIN ? up : down;
    const right = anchor.x;
    const left = anchor.x - box.width;
    const x =
      right + box.width > window.innerWidth - MARGIN && left > MARGIN
        ? left
        : right;
    return { top: Math.max(MARGIN, top), left: Math.max(MARGIN, x) };
  }

  // Below the trigger, unless there is no room below and there is more of it above.
  const below = anchor.rect.bottom + GAP;
  const above = anchor.rect.top - box.height - GAP;
  const top =
    below + box.height > window.innerHeight - MARGIN && above > MARGIN
      ? above
      : below;

  const wanted =
    anchor.align === "end" ? anchor.rect.right - box.width : anchor.rect.left;
  const left = Math.max(
    MARGIN,
    Math.min(wanted, window.innerWidth - box.width - MARGIN),
  );
  return { top, left };
}
